import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  FaSync, FaDesktop, FaMobileAlt, FaTabletAlt,
  FaChevronLeft, FaChevronRight, FaSearch, FaUserSlash, FaBan, FaTrash,
} from "react-icons/fa";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";

dayjs.extend(utc);
dayjs.extend(timezone);

const API_URL = "/api";

// ─── helpers ─────────────────────────────────────────────────────────────────

function getAuthHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("authToken")}` };
}

function getCurrentSessionId() {
  try {
    const token = localStorage.getItem("authToken");
    if (!token) return null;
    return JSON.parse(atob(token.split(".")[1])).sessionId || null;
  } catch { return null; }
}

/**
 * Convert UTC datetime string to company timezone display.
 * tzId = "India Standard Time" (Windows TZ name) → we map to IANA.
 */
const WIN_TZ_MAP = {
  "India Standard Time":           "Asia/Kolkata",
  "UTC":                           "UTC",
  "Eastern Standard Time":         "America/New_York",
  "Central Standard Time":         "America/Chicago",
  "Mountain Standard Time":        "America/Denver",
  "Pacific Standard Time":         "America/Los_Angeles",
  "GMT Standard Time":             "Europe/London",
  "Central Europe Standard Time":  "Europe/Berlin",
  "Singapore Standard Time":       "Asia/Singapore",
  "China Standard Time":           "Asia/Shanghai",
  "Tokyo Standard Time":           "Asia/Tokyo",
  "AUS Eastern Standard Time":     "Australia/Sydney",
  "Arab Standard Time":            "Asia/Riyadh",
  "Arabian Standard Time":         "Asia/Dubai",
};

function toIANA(winTZ) {
  return WIN_TZ_MAP[winTZ] || winTZ || "UTC";
}

function formatTZ(utcStr, tzId) {
  if (!utcStr) return { date: "—", time: "—" };
  const iana = toIANA(tzId);
  const d = dayjs.utc(utcStr).tz(iana);
  return { date: d.format("DD MMM YYYY"), time: d.format("HH:mm:ss") };
}

/** Format idle seconds as human-readable string */
function fmtIdle(seconds) {
  if (seconds == null || seconds < 0) return "0s";
  if (seconds < 60)  return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function DeviceIcon({ browser = "", os = "" }) {
  const b = (browser + os).toLowerCase();
  if (b.includes("iphone") || b.includes("android") || b.includes("mobile")) {
    return <FaMobileAlt className="h-4 w-4" />;
  }
  if (b.includes("ipad") || b.includes("tablet")) {
    return <FaTabletAlt className="h-4 w-4" />;
  }
  return <FaDesktop className="h-4 w-4" />;
}

function StatusBadge({ isCurrent }) {
  if (isCurrent) {
    return (
      <span style={{ borderRadius: 9999, padding: "3px 10px", fontSize: 11, fontWeight: 700, background: "#d1fae5", color: "#065f46" }}>
        ● You
      </span>
    );
  }
  return (
    <span style={{ borderRadius: 9999, padding: "3px 10px", fontSize: 11, fontWeight: 700, background: "#dbeafe", color: "#1e40af" }}>
      ● Active
    </span>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const SessionManager = () => {
  const navigate = useNavigate();
  const currentSessionId = getCurrentSessionId();

  const [sessions, setSessions]       = useState([]);
  const [tzId, setTzId]               = useState("UTC");
  const [timeoutMin, setTimeoutMin]   = useState(30);
  const [stats, setStats]             = useState({ activeSessions: 0, maxSessions: 0, availableSlots: 0, sessionTimeoutMinutes: 30 });
  const [loading, setLoading]         = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [searchTerm, setSearchTerm]   = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selected, setSelected]       = useState(new Set());   // multi-select
  const [killing, setKilling]         = useState(false);
  const [confirmKill, setConfirmKill] = useState(null);
  const [toast, setToast]             = useState(null);

  // Live idle counter — ticks every second
  const [tick, setTick] = useState(0);
  const tickRef = useRef(null);

  const itemsPerPage = 8;

  // ── Theme ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const vars = {
      "--sidebar-line-color": "#e6efee", "--sidebar-bg": "#ffffff",
      "--sidebar-text": "#0f1724",       "--accent-color": "#2b8f85",
      "--accent-hover": "#23756e",       "--page-border-color": "#e6efee",
      "--text-primary": "#334155",       "--text-muted": "#475569",
      "--page-bg": "#EEF6F5",            "--table-header-bg": "#EEF6F5",
    };
    Object.entries(vars).forEach(([k, v]) => document.documentElement.style.setProperty(k, v));

    const id = "session-manager-style";
    if (!document.getElementById(id)) {
      const s = document.createElement("style");
      s.id = id;
      s.innerHTML = `
        .primary-btn { background: var(--accent-color); color:#fff; border:none; cursor:pointer; }
        .primary-btn:hover { background: var(--accent-hover); }
        .secondary-btn { background:#fff; color:var(--text-primary); border:1px solid #e2e8f0; cursor:pointer; }
        .secondary-btn:hover { background:#f8faf9; }
        .page-active   { background:var(--accent-color); color:#fff; }
        .page-inactive { color:var(--accent-color); }
        .page-inactive:hover { background:#f7fbfa; }
        .row-selected  { background:#eff6ff !important; }
        .checkbox-cell { width: 40px; }
      `;
      document.head.appendChild(s);
    }
  }, []);

  // ── Live ticker ───────────────────────────────────────────────────────────
  useEffect(() => {
    tickRef.current = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(tickRef.current);
  }, []);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const [sessRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/sessions`,       { headers: getAuthHeaders() }),
        fetch(`${API_URL}/sessions/stats`, { headers: getAuthHeaders() }),
      ]);
      if (sessRes.ok) {
        const d = await sessRes.json();
        // Attach a fetchedAt timestamp so idle can be computed live
        const now = Date.now();
        const withTs = (d.sessions || []).map(s => ({
          ...s,
          _fetchedAt:      now,
          _idleAtFetch:    s.IdleSeconds || 0,
        }));
        setSessions(withTs);
        setTzId(d.timeZoneId || "UTC");
        setTimeoutMin(d.timeoutMinutes || 30);
      }
      if (statsRes.ok) {
        const d = await statsRes.json();
        setStats(d);
      }
      setSelected(new Set()); // clear selection on refresh
    } catch {
      showToast("error", "Failed to fetch session data.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const id = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(id);
  }, [fetchData]);

  // ── Toast ─────────────────────────────────────────────────────────────────
  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Selection ─────────────────────────────────────────────────────────────
  const killableIds = sessions
    .filter(s => s.SessionID !== currentSessionId)
    .map(s => s.SessionID);

  const allSelected = killableIds.length > 0 && killableIds.every(id => selected.has(id));

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(killableIds));
    }
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  // ── Kill actions ──────────────────────────────────────────────────────────
  const execKill = async () => {
    if (!confirmKill) return;
    setKilling(true);
    try {
      let res;
      if (confirmKill.type === "bulk") {
        res = await fetch(`${API_URL}/sessions/bulk-kill`, {
          method: "POST",
          headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ sessionIds: confirmKill.ids }),
        });
      } else if (confirmKill.type === "user") {
        res = await fetch(`${API_URL}/sessions/user/${confirmKill.id}`, {
          method: "DELETE", headers: getAuthHeaders(),
        });
      } else {
        res = await fetch(`${API_URL}/sessions/${confirmKill.id}`, {
          method: "DELETE", headers: getAuthHeaders(),
        });
      }

      if (res.ok) {
        const d = await res.json();
        showToast("success", d.message || "Session(s) terminated.");
        fetchData(true);
      } else {
        const d = await res.json();
        showToast("error", d.message || "Failed.");
      }
    } catch {
      showToast("error", "Network error.");
    } finally {
      setKilling(false);
      setConfirmKill(null);
    }
  };

  // ── Filter & paginate ─────────────────────────────────────────────────────
  const filtered = sessions.filter(s => {
    const q = searchTerm.toLowerCase();
    return !q ||
      (s.UserName    && s.UserName.toLowerCase().includes(q)) ||
      (s.Email       && s.Email.toLowerCase().includes(q)) ||
      (s.IPAddress   && s.IPAddress.includes(q)) ||
      (s.BrowserName && s.BrowserName.toLowerCase().includes(q)) ||
      (s.OSInfo      && s.OSInfo.toLowerCase().includes(q));
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paged      = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const renderPagination = () => {
    const btns  = [];
    const start = Math.max(1, Math.min(currentPage - 1, Math.max(1, totalPages - 2)));
    const end   = Math.min(totalPages, start + 2);

    btns.push(
      <button key="prev" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
        className={`p-2 ${currentPage === 1 ? "text-gray-400 cursor-not-allowed" : "page-inactive"}`}>
        <FaChevronLeft className="h-4 w-4" />
      </button>
    );
    for (let i = start; i <= end; i++) {
      btns.push(
        <button key={i} onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 font-medium text-sm ${i === currentPage ? "page-active" : "page-inactive"}`}>
          {i}
        </button>
      );
    }
    btns.push(
      <button key="next" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages || totalPages === 0}
        className={`p-2 ${currentPage >= totalPages || totalPages === 0 ? "text-gray-400 cursor-not-allowed" : "page-inactive"}`}>
        <FaChevronRight className="h-4 w-4" />
      </button>
    );
    return btns;
  };

  const usagePercent = stats.maxSessions > 0 ? Math.round((stats.activeSessions / stats.maxSessions) * 100) : 0;
  const barColor     = usagePercent >= 90 ? "#ef4444" : usagePercent >= 70 ? "#f59e0b" : "#2b8f85";

  const sidebarTheme = {
    text: "var(--sidebar-text)", muted: "var(--text-muted)",
    border: "var(--page-border-color)", sidebarBorder: "var(--sidebar-line-color)",
    background: "var(--sidebar-bg)", pageBackground: "var(--page-bg)",
    activeBg: "var(--accent-color)", profileBg: "var(--accent-color)",
    activeText: "#ffffff", accent: "var(--accent-color)",
    accentHover: "var(--accent-hover)", hoverGray: "rgba(0,0,0,0.04)",
    iconColor: "var(--accent-color)", sidebarBg: "var(--sidebar-bg)",
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "var(--page-bg)" }}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} theme={sidebarTheme}
        onLogout={() => navigate("/login")} />

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 20, right: 20, zIndex: 9999,
          background: toast.type === "success" ? "#d1fae5" : "#fee2e2",
          color: toast.type === "success" ? "#065f46" : "#991b1b",
          border: `1px solid ${toast.type === "success" ? "#6ee7b7" : "#fca5a5"}`,
          padding: "12px 20px", fontWeight: 600, fontSize: 14, maxWidth: 340,
          boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
        }}>
          {toast.message}
        </div>
      )}

      {/* Confirm Modal */}
      {confirmKill && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", padding: 28, width: 400, boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
            <h3 style={{ fontWeight: 700, fontSize: 16, color: "#0f1724", marginBottom: 8 }}>Confirm Termination</h3>
            <p style={{ color: "#475569", fontSize: 14, marginBottom: 20 }}>
              {confirmKill.type === "bulk"
                ? `This will terminate ${confirmKill.ids.length} selected session(s). Those users will be logged out immediately.`
                : confirmKill.type === "user"
                ? `This will terminate ALL sessions for ${confirmKill.label}. They will be logged out immediately.`
                : `This will terminate the session for ${confirmKill.label}. They will be logged out immediately.`}
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setConfirmKill(null)}
                style={{ padding: "8px 18px", border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", fontWeight: 600 }}>
                Cancel
              </button>
              <button onClick={execKill} disabled={killing}
                style={{ padding: "8px 18px", background: "#ef4444", color: "#fff", border: "none", cursor: "pointer", fontWeight: 600, opacity: killing ? 0.6 : 1 }}>
                {killing ? "Terminating…" : "Terminate"}
              </button>
            </div>
          </div>
        </div>
      )}

      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? "lg:ml-[90px]" : "lg:ml-[280px]"}`}>

        {/* Header */}
        <header className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-gray-300 pb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Session Manager</h1>
            <p className="mt-1 text-base text-gray-600">
              Monitor and control all active user sessions in real-time
              &nbsp;·&nbsp; <span className="font-medium text-gray-700">TZ: {tzId}</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            {selected.size > 0 && (
              <button
                onClick={() => setConfirmKill({ type: "bulk", ids: [...selected] })}
                className="flex items-center gap-2 px-5 py-2 font-medium shadow-sm"
                style={{ background: "#ef4444", color: "#fff", border: "none", cursor: "pointer" }}
              >
                <FaTrash className="h-4 w-4" />
                Kill Selected ({selected.size})
              </button>
            )}
            <button onClick={() => fetchData(true)} disabled={refreshing}
              className="flex items-center gap-2 px-5 py-2 secondary-btn font-medium shadow-sm">
              <FaSync className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} style={{ color: "var(--accent-color)" }} />
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </header>

        {/* Stats */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Active Users",    value: stats.activeSessions,        bg: "#dbeafe", color: "#1e40af" },
            { label: "Max Allowed",     value: stats.maxSessions,           bg: "#d1fae5", color: "#065f46" },
            { label: "Available Slots", value: stats.availableSlots,        bg: "#fef3c7", color: "#92400e" },
            { label: "Timeout (min)",   value: stats.sessionTimeoutMinutes, bg: "#e0e7ff", color: "#3730a3" },
          ].map(stat => (
            <div key={stat.label} className="p-5 bg-white border border-gray-300 shadow-md hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-600">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div style={{ width: 40, height: 40, borderRadius: 8, background: stat.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg style={{ color: stat.color, width: 18, height: 18 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeWidth={2} d="M5 12h14M12 5v14" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Usage bar */}
        <section className="bg-white border border-gray-300 shadow-md p-5 mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-semibold text-gray-700">License Usage — Unique Active Users</span>
            <span className="text-sm font-bold" style={{ color: barColor }}>{usagePercent}%</span>
          </div>
          <div style={{ background: "#f1f5f9", height: 10, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${usagePercent}%`, background: barColor, transition: "width 0.5s ease" }} />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {stats.activeSessions} of {stats.maxSessions} user slots in use
            &nbsp;·&nbsp; {stats.availableSlots} slot{stats.availableSlots !== 1 ? "s" : ""} remaining
            &nbsp;·&nbsp; <em>Multi-device logins by the same user count as 1 slot</em>
          </p>
        </section>

        {/* Search */}
        <section className="p-5 bg-white border border-gray-300 shadow-md mb-6">
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Search Active Sessions</label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="User, email, IP, browser, OS…"
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
                />
              </div>
            </div>
            <button className="px-5 py-2 primary-btn font-medium shadow-sm" onClick={() => setCurrentPage(1)}>
              Search
            </button>
          </div>
        </section>

        {/* Table */}
        <section className="bg-white border border-gray-300 shadow-md">
          <div className="flex justify-between items-center p-4 border-b border-gray-300 flex-wrap gap-2">
            <h2 className="text-xl font-semibold text-gray-900">
              Active Sessions
              <span className="text-sm font-normal text-gray-500 ml-2">({filtered.length})</span>
              {selected.size > 0 && (
                <span className="text-sm font-semibold ml-3" style={{ color: "#ef4444" }}>
                  {selected.size} selected
                </span>
              )}
            </h2>
            <div className="flex gap-2">{renderPagination()}</div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading sessions…</div>
          ) : paged.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No active sessions found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead style={{ backgroundColor: "var(--table-header-bg)" }} className="text-gray-600 text-xs font-semibold uppercase">
                  <tr>
                    {/* Select all checkbox */}
                    <th className="checkbox-cell px-3 py-3 text-center">
                      <input type="checkbox" checked={allSelected} onChange={toggleAll}
                        style={{ width: 15, height: 15, cursor: "pointer", accentColor: "var(--accent-color)" }} />
                    </th>
                    <th className="px-3 py-3 text-left">No.</th>
                    <th className="px-3 py-3 text-left">User</th>
                    <th className="px-3 py-3 text-left">Status</th>
                    <th className="px-3 py-3 text-left">Browser</th>
                    <th className="px-3 py-3 text-left">OS</th>
                    <th className="px-3 py-3 text-left">IP Address</th>
                    <th className="px-3 py-3 text-left">Login Time</th>
                    <th className="px-3 py-3 text-left">Last Activity</th>
                    <th className="px-3 py-3 text-left">Idle</th>
                    <th className="px-3 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paged.map(s => {
                    const isCurrent  = s.SessionID === currentSessionId;
                    const isSelected = selected.has(s.SessionID);
                    const loginFmt   = formatTZ(s.LoginTime, tzId);
                    const lastFmt    = formatTZ(s.LastActivityTime, tzId);

                    // Live idle: base idle at fetch + seconds elapsed since fetch
                    const elapsedSinceFetch = Math.floor((Date.now() - s._fetchedAt) / 1000);
                    const liveIdle          = (s._idleAtFetch || 0) + elapsedSinceFetch;
                    const idleWarn          = liveIdle > timeoutMin * 60 * 0.8;

                    return (
                      <tr key={s.SessionID}
                        className={`transition-colors ${isSelected ? "row-selected" : "hover:bg-gray-50"}`}
                        style={{ background: isCurrent && !isSelected ? "#f0fdf4" : undefined }}>

                        {/* Checkbox */}
                        <td className="checkbox-cell px-3 py-3 text-center">
                          {!isCurrent && (
                            <input type="checkbox" checked={isSelected} onChange={() => toggleOne(s.SessionID)}
                              style={{ width: 15, height: 15, cursor: "pointer", accentColor: "var(--accent-color)" }} />
                          )}
                        </td>

                        {/* Session No */}
                        <td className="px-3 py-3 font-mono text-gray-500 text-xs">{s.SessionNo}</td>

                        {/* User */}
                        <td className="px-3 py-3">
                          <div className="font-semibold text-gray-900">{s.UserName || "—"}</div>
                          <div className="text-xs text-gray-500">{s.Email || "—"}</div>
                        </td>

                        {/* Status */}
                        <td className="px-3 py-3">
                          <StatusBadge isCurrent={isCurrent} />
                        </td>

                        {/* Browser */}
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <span style={{ color: "var(--accent-color)" }}>
                              <DeviceIcon browser={s.BrowserName} os={s.OSInfo} />
                            </span>
                            <span className="text-gray-700">{s.BrowserName || "—"}</span>
                          </div>
                        </td>

                        {/* OS */}
                        <td className="px-3 py-3 text-gray-600 text-xs">{s.OSInfo || "—"}</td>

                        {/* IP */}
                        <td className="px-3 py-3 font-mono text-gray-600 text-xs">{s.IPAddress || "—"}</td>

                        {/* Login Time */}
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="text-gray-900 text-xs">{loginFmt.date}</div>
                          <div className="text-xs text-gray-500">{loginFmt.time}</div>
                        </td>

                        {/* Last Activity */}
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="text-gray-900 text-xs">{lastFmt.date}</div>
                          <div className="text-xs text-gray-500">{lastFmt.time}</div>
                        </td>

                        {/* Idle — live ticking */}
                        <td className="px-3 py-3 whitespace-nowrap">
                          {/* tick dependency ensures re-render every second */}
                          <span style={{ color: idleWarn ? "#f59e0b" : "#2b8f85", fontWeight: 600, fontSize: 12 }}
                            key={tick}>
                            {fmtIdle(liveIdle)}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="px-3 py-3">
                          <div className="flex gap-2 justify-center">
                            {!isCurrent && (
                              <>
                                <button
                                  title="Terminate this session"
                                  onClick={() => setConfirmKill({ type: "session", id: s.SessionID, label: s.UserName || s.Email })}
                                  style={{ color: "#ef4444", background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                                  <FaBan className="h-4 w-4" />
                                </button>
                                <button
                                  title="Terminate all sessions for this user"
                                  onClick={() => setConfirmKill({ type: "user", id: s.UserID, label: s.UserName || s.Email })}
                                  style={{ color: "#f59e0b", background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                                  <FaUserSlash className="h-4 w-4" />
                                </button>
                              </>
                            )}
                            {isCurrent && <span className="text-xs text-gray-400 italic">You</span>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-5 text-xs text-gray-500">
          <span><FaBan className="inline h-3 w-3 mr-1" style={{ color: "#ef4444" }} />Kill this session</span>
          <span><FaUserSlash className="inline h-3 w-3 mr-1" style={{ color: "#f59e0b" }} />Kill all sessions for user</span>
          <span><FaTrash className="inline h-3 w-3 mr-1" style={{ color: "#ef4444" }} />Kill Selected (bulk)</span>
          <span style={{ background: "#f0fdf4", padding: "1px 6px", borderRadius: 4 }}>Green highlight = your session</span>
          <span style={{ background: "#eff6ff", padding: "1px 6px", borderRadius: 4 }}>Blue highlight = selected for bulk kill</span>
        </div>

      </main>
    </div>
  );
};

export default SessionManager;